import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { estimateKeys, jobKeys, projectKeys } from '@/lib/queryKeys';
import { projectEndpoints } from '@/lib/api/endpoints';
import type {
  Project,
  ProjectEntities,
  ProjectListResponse,
  ProjectMemberJob,
  ProjectSummary,
} from '@/types/project';

export interface ProjectListParams {
  q?: string;
  status?: string;
  contactId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export function useProjects(params: ProjectListParams = {}) {
  return useApiQuery<ProjectListResponse>({
    queryKey: projectKeys.list(params as Record<string, unknown>),
    queryFn: () =>
      apiGet<ProjectListResponse>(operationsClient, projectEndpoints.list(), {
        params,
      }),
  });
}

export function useProject(id: string | undefined) {
  return useApiQuery<Project>({
    queryKey: projectKeys.detail(id ?? ''),
    queryFn: () => apiGet<Project>(operationsClient, projectEndpoints.detail(id!)),
    enabled: Boolean(id),
  });
}

export function useProjectSummary(id: string | undefined) {
  return useApiQuery<ProjectSummary>({
    queryKey: projectKeys.summary(id ?? ''),
    queryFn: () => apiGet<ProjectSummary>(operationsClient, projectEndpoints.summary(id!)),
    enabled: Boolean(id),
  });
}

export function useProjectEntities(id: string | undefined) {
  return useApiQuery<ProjectEntities>({
    queryKey: projectKeys.entities(id ?? ''),
    queryFn: () => apiGet<ProjectEntities>(operationsClient, projectEndpoints.entities(id!)),
    enabled: Boolean(id),
  });
}

export interface CreateProjectInput {
  name: string;
  contactId: string;
  description?: string;
  billToContactId?: string;
  locationId?: string;
  managerId?: string;
  startDate?: string;
  targetEndDate?: string;
  jobIds?: string[];
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useApiMutation<Project, CreateProjectInput>({
    mutationFn: (input) =>
      apiPost<Project, CreateProjectInput>(operationsClient, projectEndpoints.list(), input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all() });
      toast.success('Project created');
    },
  });
}

/**
 * PATCH semantics: omitted means "leave alone", explicit `null` means "clear".
 * The clearable fields must therefore be nullable here — sending `undefined`
 * for an emptied date field silently keeps the old value.
 */
export interface UpdateProjectInput extends Partial<
  Pick<CreateProjectInput, 'name' | 'contactId' | 'jobIds'>
> {
  status?: string;
  description?: string | null;
  billToContactId?: string | null;
  locationId?: string | null;
  managerId?: string | null;
  startDate?: string | null;
  targetEndDate?: string | null;
}

export function useUpdateProject(id: string) {
  const queryClient = useQueryClient();
  return useApiMutation<Project, UpdateProjectInput>({
    mutationFn: (input) =>
      apiPatch<Project, UpdateProjectInput>(operationsClient, projectEndpoints.detail(id), input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all() });
      toast.success('Project updated');
    },
  });
}

export function useCreateProjectFromJob() {
  const queryClient = useQueryClient();
  return useApiMutation<Project, string>({
    mutationFn: (jobId) =>
      apiPost<Project, Record<string, never>>(
        operationsClient,
        projectEndpoints.fromJob(jobId),
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all() });
      void queryClient.invalidateQueries({ queryKey: jobKeys.all() });
      toast.success('Project created from job');
    },
  });
}

export function useAttachJobs(projectId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<ProjectMemberJob[], string[]>({
    mutationFn: (jobIds) =>
      apiPost<ProjectMemberJob[], { jobIds: string[] }>(
        operationsClient,
        projectEndpoints.jobs(projectId),
        { jobIds },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.all() });
      toast.success('Jobs added to project');
    },
  });
}

export interface AttachableEstimate {
  id: string;
  estimateNumber: string;
  title: string | null;
  status: string;
  grandTotal: number;
  createdAt: string;
}

/** Unclaimed estimates for this project's customer (picker source). */
export function useAttachableEstimates(projectId: string, options: { enabled?: boolean } = {}) {
  return useApiQuery<AttachableEstimate[]>({
    queryKey: projectKeys.attachableEstimates(projectId),
    queryFn: () =>
      apiGet<AttachableEstimate[]>(
        operationsClient,
        projectEndpoints.attachableEstimates(projectId),
      ),
    enabled: Boolean(projectId) && (options.enabled ?? true),
  });
}

export function useAttachEstimates(projectId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<{ attachedJobs: unknown[] }, string[]>({
    mutationFn: (estimateIds) =>
      apiPost<{ attachedJobs: unknown[] }, { estimateIds: string[] }>(
        operationsClient,
        projectEndpoints.estimates(projectId),
        { estimateIds },
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: estimateKeys.all() });
      const jobs = result?.attachedJobs?.length ?? 0;
      toast.success(
        jobs > 0
          ? `Estimate added — its ${jobs} linked job${jobs === 1 ? '' : 's'} came along`
          : 'Estimate added to project',
      );
    },
  });
}

export function useDetachEstimate(projectId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<void, string>({
    mutationFn: (estimateId) =>
      apiDelete<void>(operationsClient, projectEndpoints.detachEstimate(projectId, estimateId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: estimateKeys.all() });
      toast.success('Estimate removed from project');
    },
  });
}

export function useDetachJob(projectId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<void, string>({
    mutationFn: (jobId) =>
      apiDelete<void>(operationsClient, projectEndpoints.detachJob(projectId, jobId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: jobKeys.all() });
      toast.success('Job removed from project');
    },
  });
}
